// useAggressiveStream — polls /api/feed-suggestions/aggressive-status/{jobId}
// on a 1s cadence until the streaming Aggressive job reaches `complete` or
// expires (404 from the status endpoint). Exposes the merged per-candidate
// Tier 1 results so the StrategyCard can overlay verdicts in place.
//
// Lifecycle:
//   - On jobId change → reset state, start polling.
//   - On status === "complete" → stop polling (interval cleared).
//   - On 404 → status = "expired" (operator hits Refresh to re-POST suggestions).
//   - On network error → exponential backoff capped at 5s; after 3 consecutive
//     failures → status = "error" (caller can show a retry affordance).
//   - On unmount → interval cleared, in-flight fetch aborted via AbortController.
//
// Polling uses ApiError from lib/api to distinguish HTTP 404 from network errors.

import { useEffect, useRef, useState } from "react";
import { ApiError, getAggressiveStatus } from "@/lib/api";
import type { Tier1Result } from "@/lib/types";

export type AggressiveStreamStatus =
  | "idle"
  | "computing"
  | "complete"
  | "expired"
  | "error";

export interface AggressiveStreamState {
  status: AggressiveStreamStatus;
  total: number;
  completed: number;
  results: Record<string, Tier1Result | null>;
  errorMessage: string | null;
}

const POLL_INTERVAL_MS = 1000;
const BACKOFF_CAP_MS = 5000;
const MAX_CONSECUTIVE_FAILURES = 3;

const INITIAL: AggressiveStreamState = {
  status: "idle",
  total: 0,
  completed: 0,
  results: {},
  errorMessage: null,
};

export function useAggressiveStream(
  jobId: string | null,
): AggressiveStreamState {
  const [state, setState] = useState<AggressiveStreamState>(INITIAL);
  // Mutable timer + cancellation refs survive across renders without
  // triggering effect re-runs.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const failuresRef = useRef(0);

  useEffect(() => {
    // Reset whenever jobId changes (including to null).
    setState(jobId
      ? { ...INITIAL, status: "computing" }
      : INITIAL);
    failuresRef.current = 0;

    if (!jobId) return;

    let cancelled = false;

    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const schedule = (delayMs: number) => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        void poll();
      }, delayMs);
    };

    const poll = async () => {
      if (cancelled) return;
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const resp = await getAggressiveStatus(jobId, { signal: ctrl.signal });
        if (cancelled) return;
        failuresRef.current = 0;
        const done = resp.status === "complete";
        setState({
          status: done ? "complete" : "computing",
          total: resp.total,
          completed: resp.completed,
          results: resp.results,
          errorMessage: null,
        });
        if (!done) schedule(POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if ((e as Error).name === "AbortError") return;
        if (e instanceof ApiError && e.status === 404) {
          setState((prev) => ({
            ...prev,
            status: "expired",
            errorMessage: "Aggressive results expired — refresh to recompute.",
          }));
          return;
        }
        failuresRef.current += 1;
        if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
          setState((prev) => ({
            ...prev,
            status: "error",
            errorMessage: e instanceof Error ? e.message : "polling failed",
          }));
          return;
        }
        // Exponential backoff, capped — 1s, 2s, 4s ... but we cap at 5s.
        const backoff = Math.min(
          POLL_INTERVAL_MS * Math.pow(2, failuresRef.current),
          BACKOFF_CAP_MS,
        );
        schedule(backoff);
      }
    };

    // Kick off the first poll immediately so the UI doesn't sit blank for 1s.
    void poll();

    return () => {
      cancelled = true;
      clearTimer();
      abortRef.current?.abort();
    };
  }, [jobId]);

  return state;
}
